import { ButtonDock } from "@ui/components/ButtonDock";
import { Dialog } from "@ui/components/Dialog";
import type { IllustrationName } from "@ui/components/Illustration";
import { Illustration } from "@ui/components/Illustration";
import { StandardButton } from "@ui/components/StandardButton";
import type { ReactNode } from "react";

/** Shared by both placements so the two copies cannot drift apart. */
const ILLUSTRATION_SIZE = "h-[153px] w-[168px] object-contain";

export interface ConfirmDialogProps {
  /** Dialog title (the mobile sheet's title bar; also the desktop header when no illustration). */
  title: string;
  /** Text explaining what confirming will do. */
  description: ReactNode;
  /** Confirm button label. */
  confirmLabel: string;
  /** Renders a secondary cancel button (closes the dialog) when provided. */
  cancelLabel?: string;
  /** Whether the dialog is open */
  isOpen: boolean;
  /** Whether the confirm action is in progress */
  isPending?: boolean;
  /** Called when the confirm button is pressed */
  onConfirm: () => void;
  /** Called when the dialog should close (close button, cancel, Escape) */
  onOpenChange: (isOpen: boolean) => void;
  /** Confirm button treatment: "danger" (pink) for destructive actions, "default" (green) otherwise. */
  tone?: "danger" | "default";
  /**
   * Decorative illustration. On desktop it overflows the card's top edge (with
   * a corner close button replacing the header); on mobile it renders inline
   * above the description.
   */
  illustration?: IllustrationName;
}

/**
 * Confirmation dialog (`role="alertdialog"`, size `sm`) composed from the
 * system Dialog: Header (or a desktop corner close when an illustration
 * overflows the top edge), Body with the description, and a ButtonDock with
 * confirm + optional cancel. Alertdialog semantics harden dismissal — scrim
 * tap and swipe don't dismiss; Escape, the close X, and cancel do.
 *
 * With `illustration`, `md:overflow-visible` releases the panel's clip so the
 * desktop illustration can cross the card's top edge; mobile keeps the clip.
 */
export function ConfirmDialog({
  title,
  description,
  confirmLabel,
  cancelLabel,
  isOpen,
  isPending = false,
  onConfirm,
  onOpenChange,
  tone = "default",
  illustration,
}: ConfirmDialogProps) {
  return (
    <Dialog.Root onOpenChange={onOpenChange} isOpen={isOpen} role="alertdialog" size="sm">
      <Dialog.Content className={illustration ? "md:overflow-visible" : undefined}>
        {illustration ? (
          <>
            {/* Mobile: sheet title bar with the close X (the focusable cancel path).
                Desktop part is hidden — the corner button below dismisses there. */}
            <Dialog.Header className="md:hidden" title={title} />
            {/* Desktop only: the corner close over the overflowing illustration. */}
            <Dialog.CloseButton className="absolute end-2 top-2 z-10 me-0 mt-0 hidden md:flex" />
            {/* Desktop placement, outside the body: Silk forces the scroll
                container to `position: relative` and to clip its overflow, so
                an illustration inside the body can neither anchor to the card
                nor cross its edge. The mobile copy stays in the body, where it
                scrolls with the description. */}
            <Illustration
              className={`${ILLUSTRATION_SIZE} absolute start-1/2 -top-[17px] hidden -translate-x-1/2 md:block`}
              name={illustration}
            />
          </>
        ) : (
          <Dialog.Header title={title} />
        )}

        <Dialog.Body centered={Boolean(illustration)}>
          {illustration ? (
            <div className="flex flex-col items-center gap-5 max-md:pt-5">
              <Illustration className={`${ILLUSTRATION_SIZE} shrink-0 md:hidden`} name={illustration} />
              {/* Reserves the space the desktop illustration occupies above. */}
              <div aria-hidden="true" className="hidden h-[100px] shrink-0 md:block" />
              <p className="w-full text-center text-lg/6 text-neutral-600">{description}</p>
            </div>
          ) : (
            <p className="text-base/6 text-neutral-600">{description}</p>
          )}
        </Dialog.Body>

        <Dialog.Footer>
          <ButtonDock
            primaryAction={
              <StandardButton
                className="w-full"
                color={tone === "danger" ? "pink" : "green"}
                isPending={isPending}
                onPress={onConfirm}
                size="44"
              >
                {confirmLabel}
              </StandardButton>
            }
            secondaryAction={
              cancelLabel ? (
                <StandardButton color="neutral" hierarchy="secondary" onPress={() => onOpenChange(false)} size="44">
                  {cancelLabel}
                </StandardButton>
              ) : undefined
            }
          />
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog.Root>
  );
}
